import * as React from "react";
import {Slot} from "@radix-ui/react-slot";
import {cva} from "class-variance-authority";

import {cn} from "lib/utils";

const buttonVariants = cva(
  // Furniture, not headline: the label is sans, tracked, and never shouts.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans font-semibold select-none " +
    "transition-[transform,box-shadow,background-color,color] duration-150 ease-out " +
    "disabled:pointer-events-none disabled:opacity-50 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-paper " +
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        /**
         * Newspaper furniture is printed, not moulded: flat ink on paper with
         * a square-ish corner. Weight and colour carry the hierarchy, and the
         * only motion is a small press.
         */
        default:
          "rounded-md bg-masthead text-masthead-foreground hover:bg-masthead/90 active:scale-[0.98]",
        /** The main action on a view. Solid plate, nothing else competing. */
        primary:
          "rounded-md bg-ink text-paper hover:bg-ink/88 active:scale-[0.98]",
        /** Secondary actions: a rule around them, paper behind. */
        outline:
          "rounded-md border border-rule-strong bg-transparent text-ink hover:bg-surface-2 active:scale-[0.98]",
        ghost:
          "rounded-md text-ink-secondary hover:bg-surface-2 hover:text-ink active:scale-[0.98]",
        link: "text-masthead underline-offset-4 hover:underline",
        /* The recommender speaks in blue pencil. */
        pencil:
          "rounded-md border border-pencil/30 bg-pencil-muted text-pencil hover:border-pencil/50 active:scale-[0.98]",
      },
      size: {
        sm: "h-8 px-3 text-meta",
        md: "h-10 px-4 text-[0.8125rem]",
        lg: "h-12 px-6 text-body",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {variant: "default", size: "md"},
  }
);

const Button = React.forwardRef(
  ({className, variant, size, asChild = false, ...props}, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({variant, size}), className)}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export {Button, buttonVariants};
